import type { ExternalMCPServer } from "@gram/client/models/components/externalmcpserver.js";
import type { PulseMCPServer } from "../hooks";
import type { FilterState } from "./useFilterState";

/**
 * Parsed metadata from a server for display and filtering.
 */
export interface ParsedServerMetadata {
  // Usage stats
  visitorsWeek: number;
  visitorsMonth: number;
  visitorsTotal: number;
  /** Estimated weekly breakdown for sparkline (4 values, oldest to newest) */
  weeklyData: number[];

  // Status
  isOfficial: boolean;
  isLatest: boolean;
  status?: string;

  // Auth
  authType: string;
  authTypeDisplay: string;
  /** True when the server needs manual auth setup (no DCR support). */
  requiresManualSetup: boolean;

  // Tools
  toolCount: number;
  isReadOnly: boolean;

  // Dates
  publishedAt?: Date;
  updatedAt?: Date;
}

export function isPulseMcpServer(
  server: ExternalMCPServer,
): server is PulseMCPServer {
  return !!server.meta;
}

/**
 * Whether connecting to a server requires manual auth setup. A server is
 * "automatic" only when its OAuth authorization server advertises a dynamic
 * client registration endpoint (DCR, RFC 7591), which Speakeasy registers a client
 * against on the fly. Everything else — OAuth 2.0 without DCR, API-key servers,
 * etc. — needs the user to supply credentials manually. Backed by the
 * server-computed `supports_dcr` flag from the Pulse catalog payload.
 */
export function requiresManualSetup(server: PulseMCPServer): boolean {
  return !server.supportsDcr;
}

export type PulseMcpAuthType = "none" | "apikey" | "oauth" | "other";

/**
 * Extract the authentication type from server metadata.
 */
export function extractAuthType(server: PulseMCPServer): PulseMcpAuthType {
  const versionMeta = server.meta?.["com.pulsemcp/server-version"];
  const remote = versionMeta?.["remotes[0]"];
  const authOptions = remote?.authOptions?.[0];

  if (!authOptions || !authOptions.type) {
    return "none";
  }

  const authTypeLower = authOptions.type.toLowerCase();

  if (authTypeLower === "none" || authTypeLower === "") {
    return "none";
  }
  if (authTypeLower.includes("oauth")) {
    return "oauth";
  }
  if (authTypeLower.includes("api") || authTypeLower.includes("key")) {
    return "apikey";
  }
  return "other";
}

/**
 * Get a human-readable display string for auth type.
 */
function getAuthTypeDisplay(authType: string): string {
  switch (authType) {
    case "none":
      return "No Auth";
    case "apikey":
      return "API Key";
    case "oauth":
      return "OAuth";
    default:
      return "Auth Required";
  }
}

/**
 * Estimate weekly breakdown from monthly and weekly totals.
 * Returns 4 values representing the last 4 weeks (oldest to newest).
 */
function estimateWeeklyData(
  visitorsWeek: number,
  visitorsMonth: number,
): number[] {
  if (visitorsMonth === 0) {
    return [0, 0, 0, 0];
  }

  // The most recent week is known
  const recentWeek = visitorsWeek;
  // Remaining visitors distributed across the other 3 weeks
  const remaining = Math.max(0, visitorsMonth - recentWeek);
  const avgOther = Math.round(remaining / 3);

  // Add slight variation for visual interest (but keep it realistic)
  const variance = Math.round(avgOther * 0.15);
  return [
    Math.max(0, avgOther - variance),
    avgOther,
    Math.max(0, avgOther + variance),
    recentWeek,
  ];
}

/**
 * Parse all relevant metadata from a server.
 */
export function parseServerMetadata(
  server: PulseMCPServer,
): ParsedServerMetadata {
  const serverMeta = server.meta?.["com.pulsemcp/server"];
  const versionMeta = server.meta?.["com.pulsemcp/server-version"];

  const visitorsWeek = serverMeta?.visitorsEstimateMostRecentWeek ?? 0;
  const visitorsMonth = serverMeta?.visitorsEstimateLastFourWeeks ?? 0;
  const visitorsTotal = serverMeta?.visitorsEstimateTotal ?? 0;

  const authType = extractAuthType(server);

  return {
    visitorsWeek,
    visitorsMonth,
    visitorsTotal,
    weeklyData: estimateWeeklyData(visitorsWeek, visitorsMonth),
    isOfficial: serverMeta?.isOfficial ?? false,
    isLatest: versionMeta?.isLatest ?? false,
    status: versionMeta?.status,
    authType,
    authTypeDisplay: getAuthTypeDisplay(authType),
    requiresManualSetup: requiresManualSetup(server),
    toolCount: server.toolCount,
    isReadOnly: server.isReadOnly,
    publishedAt: versionMeta?.publishedAt
      ? new Date(versionMeta.publishedAt)
      : undefined,
    updatedAt: versionMeta?.updatedAt
      ? new Date(versionMeta.updatedAt)
      : undefined,
  };
}

/**
 * Check if a date is within a given range.
 */
function isWithinRange(date: Date | undefined, range: string): boolean {
  if (!date || range === "any") return true;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  switch (range) {
    case "week":
      return diffDays <= 7;
    case "month":
      return diffDays <= 30;
    case "year":
      return diffDays <= 365;
    default:
      return true;
  }
}

/**
 * Filter and sort servers based on the current filter state.
 * Optionally applies a search query for client-side filtering.
 */
export function filterAndSortServers(
  servers: PulseMCPServer[],
  filterState: FilterState,
  searchQuery?: string,
): PulseMCPServer[] {
  // Parse metadata for all servers
  const serversWithMeta = servers.map((server) => ({
    server,
    metadata: parseServerMetadata(server),
    displayName: server.title ?? server.registrySpecifier,
  }));

  let filtered = serversWithMeta;

  // Apply search filter (client-side)
  if (searchQuery && searchQuery.trim() !== "") {
    const query = searchQuery.toLowerCase().trim();
    filtered = filtered.filter((s) => {
      const title = (s.server.title ?? "").toLowerCase();
      const specifier = s.server.registrySpecifier.toLowerCase();
      const description = (s.server.description ?? "").toLowerCase();
      return (
        title.includes(query) ||
        specifier.includes(query) ||
        description.includes(query)
      );
    });
  }

  // Apply category filter
  if (filterState.category === "popular") {
    filtered = filtered.filter((s) => s.metadata.visitorsMonth >= 100);
  }
  // "all" - no filter

  // Apply granular filters
  const { filters } = filterState;

  // Auth type filter
  if (filters.authTypes.length > 0) {
    filtered = filtered.filter((s) =>
      filters.authTypes.includes(
        s.metadata.authType as "none" | "apikey" | "oauth" | "other",
      ),
    );
  }

  // Setup filter (manual vs automatic / DCR)
  if (filters.setupTypes.length > 0) {
    filtered = filtered.filter((s) => {
      const manual = s.metadata.requiresManualSetup;
      return (
        (filters.setupTypes.includes("manual") && manual) ||
        (filters.setupTypes.includes("auto") && !manual)
      );
    });
  }

  // Tool behavior filter
  if (filters.toolBehaviors.length > 0) {
    filtered = filtered.filter((s) => {
      if (filters.toolBehaviors.includes("readonly") && s.metadata.isReadOnly)
        return true;
      if (filters.toolBehaviors.includes("write") && !s.metadata.isReadOnly)
        return true;
      return false;
    });
  }

  // Popularity filter
  if (filters.minUsers > 0) {
    filtered = filtered.filter(
      (s) => s.metadata.visitorsMonth >= filters.minUsers,
    );
  }

  // Updated range filter
  if (filters.updatedRange !== "any") {
    filtered = filtered.filter((s) =>
      isWithinRange(s.metadata.updatedAt, filters.updatedRange),
    );
  }

  // Tool count filter
  if (filters.minTools > 0) {
    filtered = filtered.filter((s) => s.metadata.toolCount >= filters.minTools);
  }

  // Apply sorting
  switch (filterState.sort) {
    case "popular":
      filtered = filtered.sort(
        (a, b) => b.metadata.visitorsMonth - a.metadata.visitorsMonth,
      );
      break;
    case "recent":
      filtered = filtered.sort((a, b) => {
        const dateA = a.metadata.publishedAt?.getTime() ?? 0;
        const dateB = b.metadata.publishedAt?.getTime() ?? 0;
        return dateB - dateA;
      });
      break;
    case "updated":
      filtered = filtered.sort((a, b) => {
        const dateA = a.metadata.updatedAt?.getTime() ?? 0;
        const dateB = b.metadata.updatedAt?.getTime() ?? 0;
        return dateB - dateA;
      });
      break;
    case "alphabetical":
      filtered = filtered.sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      );
      break;
    case "alphabetical-desc":
      filtered = filtered.sort((a, b) =>
        b.displayName.localeCompare(a.displayName),
      );
      break;
  }

  return filtered.map((s) => s.server);
}
